/* eslint-disable @mizdra/layout-shift/require-size-attributes */
import React from 'react';
import {
  Drawer, DrawerHeader, DrawerBody, Skeleton, SkeletonBlock, SkeletonLine
} from '@one-thd/sui-atomic-components';
import {
  shape,
  fragment,
  string as stringType,
  useDataModel,
  customType,
  arrayOf,
  params
} from '@thd-nucleus/data-sources';
import { RichText } from '@thd-olt-component-react/rich-text';
import PropTypes from 'prop-types';

export const HotspotDrawer = ({ componentId, headline, isOpen, setIsOpen }) => {

  const { data, loading, error } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass: 'ImageHotspot',
    },
    skip: !isOpen,
    ssr: false,
    // Do not change the fetchPolicy as it WILL cause data issues due to conflicting component queries
    fetchPolicy: 'network-only',
  });

  if ((!data && !loading) || error) {
    return null;
  }

  const hotspotAction = data?.component?.hotspotActions?.[0];
  const drawerContent = hotspotAction?.drawer?.drawerContent;

  // eslint-disable-next-line array-callback-return, consistent-return
  const DrawerBodyContent = drawerContent?.map((contentItem, index) => {
    if (contentItem?.__typename === 'Description_New') {
      return <RichText key={index} content={contentItem?.description} />;
    }

    if (contentItem?.__typename === 'DrawerImage' && contentItem?.image !== null) {
      const imageUrl = contentItem?.image?.damDownloadedContent?.url
        || contentItem?.image?.damContentSelector?.assetData?.[0]?.selectedImageUrl;
      const altText = contentItem?.image?.title || 'Hotspot drawer image';

      return <img key={index} src={imageUrl} alt={altText} className="sui-w-full" />;
    }
  });

  return (
    <div data-testid="hotspot-drawer">
      <Drawer open={isOpen} onClose={() => setIsOpen(false)}>
        <DrawerHeader
          height="tight"
          title={headline}
          onClose={() => setIsOpen(false)}
        />
        <DrawerBody>{loading ? (
          <Skeleton grow>
            <SkeletonBlock apect="wide" />
            <SkeletonLine numberOfLines={10} />
          </Skeleton>
        ) : DrawerBodyContent}
        </DrawerBody>
      </Drawer>
    </div>
  );
};

HotspotDrawer.propTypes = {
  componentId: PropTypes.string.isRequired,
  headline: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};

HotspotDrawer.dataModel = {
  component: params({
    componentClass: customType('ComponentClass').enum(['ImageHotspot']).isRequired(),
    id: stringType().isRequired(),
  }).shape({
    ImageHotspot: fragment().shape({
      hotspotActions: arrayOf(shape({
        drawer: shape({
          headline: stringType(),
          drawerContent: arrayOf(shape({
            __typename: stringType(),
            // Inline fragment for Description_New type
            Description_New: fragment().shape({
              descriptionText: stringType(),
              description: stringType(),
              title: stringType()
            }),
            // Inline fragment for DrawerImage type - matching GraphQL schema
            DrawerImage: fragment().shape({
              componentName: stringType(),
              id: stringType(),
              image: shape({
                title: stringType(),
                damDownloadedContent: shape({
                  url: stringType(),
                }),
                damContentSelector: shape({
                  assetData: arrayOf(shape({
                    selectedImageUrl: stringType(),
                  }))
                })
              })
            })
          }))
        }),
      }))
    })
  })
};

export default HotspotDrawer;